import { useState } from 'react';
import { getStopLogistics } from '../stopLogistics';

// Surfaced per stop on My Trip. Country/city-specific logistics — currency,
// plug, water, transit, bag storage, eSIM, concierge prep, photo spots,
// what to avoid. The stuff a real traveler needs but no booking site
// surfaces in one place.
export default function StopLogisticsCard({ city, stopIndex }) {
  const [open, setOpen] = useState(stopIndex === 0); // first stop open by default
  const logistics = getStopLogistics(city);

  if (!logistics) {
    return (
      <div className="bg-brand-ink/5 border border-brand-ink/10 rounded-2xl p-4 text-xs text-brand-ink/65">
        We don't have detailed logistics for <strong>{city}</strong> yet. Coming soon — for now, check the Country Reciprocity Schedule (visa), XE.com (currency), and your hotel concierge.
      </div>
    );
  }

  return (
    <div className="bg-navy-light border border-brand-ink/10 rounded-2xl overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full p-4 flex items-center justify-between text-left hover:bg-brand-ink/5 transition"
      >
        <div className="flex items-baseline gap-2">
          <span className="text-base font-bold text-brand-ink">{city} logistics</span>
          <span className="text-[10px] text-brand-ink/55">· {logistics.country} · {logistics.currency.symbol}{logistics.currency.code}</span>
        </div>
        <span className="text-brand-ink/45">{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-brand-ink/10 pt-3 text-xs">

          {/* Quick facts grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <Fact label="Currency" value={`${logistics.currency.symbol} ${logistics.currency.code}`} note={logistics.currency.tip} />
            <Fact label="Tipping" value={logistics.tipping?.split(' ').slice(0, 4).join(' ') + '…'} note={logistics.tipping} />
            <Fact label="Plug" value={logistics.plug} note={`${logistics.voltage}`} />
            <Fact label="Tap water" value={logistics.tapWater?.split('.')[0] || 'Check locally'} note={logistics.tapWater} />
            <Fact label="Emergency" value={logistics.emergency} />
            <Fact label="Visa" value={logistics.visa?.includes('Schengen') ? 'Schengen' : 'See note'} note={logistics.visa} />
          </div>

          {/* Language essentials */}
          {logistics.language && (
            <div className="bg-teal/8 border border-teal/20 rounded-md p-2.5">
              <div className="text-[10px] font-bold text-teal uppercase tracking-wider mb-1">Language essentials ({logistics.language.code.toUpperCase()})</div>
              <div className="text-brand-ink/80 leading-snug">{logistics.language.essentials}</div>
            </div>
          )}

          {/* Transit */}
          {logistics.transit && (
            <div>
              <div className="font-bold text-brand-ink/85 mb-1">Getting around</div>
              <div className="space-y-1">
                {Object.entries(logistics.transit).map(([mode, desc]) => (
                  <div key={mode} className="flex gap-2">
                    <span className="text-[10px] uppercase font-bold text-brand-ink/55 w-16 shrink-0">{mode.replace(/_/g, ' ')}</span>
                    <span className="text-brand-ink/80 leading-snug flex-1">{desc}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* eSIM / connectivity */}
          {logistics.eSIM && (
            <div className="bg-brand-ink/5 border border-brand-ink/10 rounded-md p-2.5">
              <div className="text-[10px] font-bold text-brand-ink/55 uppercase tracking-wider mb-1">📱 eSIM / connectivity</div>
              <div className="text-brand-ink/80 leading-snug">{logistics.eSIM}</div>
            </div>
          )}

          {/* Bag storage */}
          {logistics.bagStorage && logistics.bagStorage.length > 0 && (
            <div>
              <div className="font-bold text-brand-ink/85 mb-1">Bag storage in {city}</div>
              <div className="space-y-1">
                {logistics.bagStorage.map((b, i) => (
                  <div key={i} className="flex items-center gap-2 bg-brand-ink/5 rounded-md p-2">
                    <span className="text-base">🔐</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-brand-ink/85 font-medium">{b.name}</div>
                      <div className="text-[10px] text-brand-ink/65">{b.cost}{b.notes ? ' · ' + b.notes : ''}</div>
                    </div>
                    {b.url && (
                      <a href={b.url} target="_blank" rel="noopener noreferrer" className="text-teal text-[11px] font-semibold hover:underline shrink-0">
                        Reserve ↗
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Arrival + departure day tips */}
          <div className="grid sm:grid-cols-2 gap-2">
            {logistics.arrivalDayTip && (
              <div className="bg-teal/5 border border-teal/20 rounded-md p-2.5">
                <div className="text-[10px] font-bold text-teal uppercase tracking-wider mb-1">📥 Arrival day</div>
                <div className="text-brand-ink/80 leading-snug">{logistics.arrivalDayTip}</div>
              </div>
            )}
            {logistics.departureDayTip && (
              <div className="bg-rose-500/5 border border-rose-500/20 rounded-md p-2.5">
                <div className="text-[10px] font-bold text-rose-300 uppercase tracking-wider mb-1">📤 Departure day</div>
                <div className="text-brand-ink/80 leading-snug">{logistics.departureDayTip}</div>
              </div>
            )}
          </div>

          {/* Concierge prep */}
          {logistics.conciergePrep && logistics.conciergePrep.length > 0 && (
            <div className="bg-indigo-500/5 border border-indigo-500/20 rounded-md p-2.5">
              <div className="text-[10px] font-bold text-indigo-300 uppercase tracking-wider mb-1">🛎️ Ask the concierge to set up</div>
              <ul className="space-y-0.5 list-disc list-inside text-brand-ink/80 leading-snug">
                {logistics.conciergePrep.map((p, i) => <li key={i}>{p}</li>)}
              </ul>
            </div>
          )}

          {/* Avoid these */}
          {logistics.avoidThese && (
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-md p-2.5">
              <div className="text-[10px] font-bold text-amber-300 uppercase tracking-wider mb-1">⚠️ Avoid these</div>
              <div className="text-brand-ink/80 leading-snug">{logistics.avoidThese}</div>
            </div>
          )}

          {/* Photo spots */}
          {logistics.photoSpots && (
            <div className="bg-gold/5 border border-gold/20 rounded-md p-2.5">
              <div className="text-[10px] font-bold text-gold uppercase tracking-wider mb-1">📸 Photo spots</div>
              <div className="text-brand-ink/80 leading-snug">{logistics.photoSpots}</div>
            </div>
          )}

        </div>
      )}
    </div>
  );
}

function Fact({ label, value, note }) {
  return (
    <div className="bg-brand-ink/5 border border-brand-ink/10 rounded-md p-2" title={note}>
      <div className="text-[10px] font-bold text-brand-ink/55 uppercase tracking-wider">{label}</div>
      <div className="text-brand-ink/85 font-medium mt-0.5 truncate">{value}</div>
    </div>
  );
}
